import { debounce } from "@qlik/qlik/src/lib/utils";
import { useLayoutEffect, useEffect, useState } from "react";

const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

/**
 * A custom React hook that determines whether the current screen width is less than a specified threshold (mobile screen).
 * It listens to the window resize event and updates the state accordingly.
 * The resize event handler is debounced to improve performance.
 *
 * @function
 * @returns {boolean} - Returns true if the screen width is less than the mobile screen width threshold (768px), otherwise false.
 *
 * @example
 * const isMobileScreen = useIsMobileScreen();
 * if (isMobileScreen) {
 *   // Render mobile-specific component
 * } else {
 *   // Render desktop-specific component
 * }
 */
const useIsMobileScreen = () => {
    const MOBILE_SCREEN_WIDTH = 768;
    const DEBOUNCE_THRESHOLD = 250;
    const [isMobileScreen, setIsMobileScreen] = useState(false);

    useIsomorphicLayoutEffect(() => {
        const handleResize = () => {
            setIsMobileScreen(window.innerWidth < MOBILE_SCREEN_WIDTH);
        };
        // Creating a debounced version of the resize handler
        const debouncedResizeHandler = debounce(handleResize, DEBOUNCE_THRESHOLD);

        // Adding the debounced resize handler as an event listener for the resize event
        window.addEventListener("resize", debouncedResizeHandler);

        // Returning a cleanup function that removes the event listener when the component unmounts
        return () => window.removeEventListener("resize", debouncedResizeHandler);
    }, []);

    return isMobileScreen;
};

export default useIsMobileScreen;
